// src/components/AIChat.jsx
import React, { useState, useRef, useEffect } from "react";
import { askGemini } from "../utils/gemini";
import { getLiveAppCtx, getPublicAppCtx } from "../utils/aiContext";
import { useAuth } from "../context/AuthContext";
import { theme } from "../utils/theme";

const C = theme.colors;

const AIChat = ({ isPublic = false }) => {
  const { role, studentId } = useAuth();
  const [messages, setMessages] = useState([
    { role: "ai", text: isPublic 
      ? "Namaste! Welcome to Pankaj Computers. I am your Life AI Assistant. How can I help you learn more about our courses and wellness programs?"
      : "Namaste! I am your Life AI Assistant. How can I help you today?" 
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const endRef = useRef(null);

  const scrollToBottom = () => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);

    try {
      const context = isPublic ? await getPublicAppCtx() : await getLiveAppCtx(role, studentId);
      const response = await askGemini(userMsg, context);
      setMessages(prev => [...prev, { role: "ai", text: response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: "ai", text: "Error: " + err.message, isError: true }]);
    } finally {
      setLoading(false);
    }
  };

  const containerStyle = isFullscreen ? {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    width: "100vw",
    height: "100vh",
    maxWidth: "none",
    borderRadius: 0,
    background: C.surface
  } : {
    display: "flex", 
    flexDirection: "column", 
    height: "100%", 
    width: "100%",
    maxWidth: 800, 
    margin: "0 auto", 
    background: C.surface, 
    borderRadius: 16, 
    border: `1px solid ${C.border}`, 
    overflow: "hidden", 
    boxShadow: theme.shadows.soft
  };

  const [isListening, setIsListening] = useState(false);

  const handleVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return alert("Speech recognition not supported in this browser.");
    
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      console.log("Listening started...");
      setIsListening(true);
    };
    recognition.onend = () => {
      console.log("Listening ended...");
      setIsListening(false);
    };
    
    recognition.onresult = (event) => {
      const resultIndex = event.results.length - 1; 
      const transcript = event.results[resultIndex][0].transcript;
      setInput(prev => (prev + " " + transcript).trim());
      recognition.stop();
    };

    recognition.onerror = (event) => {
      console.error("Speech error:", event.error);
      setIsListening(false);
    };

    recognition.start();
  };

  return (
    <div className="fade" style={containerStyle}>
      {/* Header */}
      <div style={{ padding: "12px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: C.accentSoft }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#10B981" }} />
          <div style={{ fontSize: 15, fontWeight: 700, color: C.accent }}>✨ Life AI Assistant</div>
        </div>
        <button 
          onClick={() => setIsFullscreen(!isFullscreen)}
          style={{ background: "none", border: "none", color: C.accent, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
        >
          {isFullscreen ? "✕ EXIT FULLSCREEN" : "⛶ FULLSCREEN"}
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ 
            alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
            maxWidth: "85%",
            padding: "12px 16px",
            borderRadius: msg.role === "user" ? "16px 16px 2px 16px" : "16px 16px 16px 2px",
            background: msg.role === "user" ? C.accent : (msg.isError ? "#FEF2F2" : C.bg),
            color: msg.role === "user" ? "#fff" : (msg.isError ? C.red : C.text),
            fontSize: 14,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
          }}>
            {msg.text}
          </div>
        ))}
        {loading && (
          <div style={{ alignSelf: "flex-start", background: C.bg, padding: "12px 16px", borderRadius: "16px 16px 16px 2px", color: C.muted, fontSize: 13, fontStyle: "italic" }}>
            Life AI is typing...
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} style={{ padding: 16, borderTop: `1px solid ${C.border}`, display: "flex", gap: 10, background: C.bg }}>
        <button type="button" onClick={handleVoiceInput} style={{ background: "none", border: "none", cursor: "pointer", color: isListening ? C.red : C.muted, padding: 4, display: "flex", alignItems: "center" }} title="Voice Input">🎤</button>
        
        <input 
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Ask anything..."
          style={{ 
            flex: 1, 
            padding: "12px 16px", 
            borderRadius: 10, 
            border: `1px solid ${C.border}`, 
            outline: "none", 
            fontSize: 14,
            fontFamily: "inherit",
            background: C.surface
          }}
        />
        <button 
          type="submit" 
          disabled={loading || !input.trim()}
          style={{ 
            padding: "0 20px", 
            borderRadius: 10, 
            background: C.accent, 
            color: "#fff", 
            border: "none", 
            fontWeight: 600, 
            cursor: "pointer",
            transition: ".2s",
            opacity: (loading || !input.trim()) ? 0.6 : 1
          }}
        >
          Send
        </button>
      </form>
    </div>
  );
};

export default AIChat;
